import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, X, Trash2, MailPlus, Clock, ShieldCheck, Send } from "lucide-react";
import api, { safeCopy, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const labelCls = "font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5";

export default function AdminDevTeams({ users, onChanged }) {
  const [teams, setTeams] = useState([]);
  const [invites, setInvites] = useState([]);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invName, setInvName] = useState("");
  const [invTeams, setInvTeams] = useState([]);
  const [memberPick, setMemberPick] = useState({});
  const [confirmDelete, setConfirmDelete] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/dev/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/admin/dev/invites").then((r) => setInvites(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(ok || r.data?.message || "Done");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const createTeam = () => act(async () => {
    await api.post("/admin/dev/teams", { name: newName, description: newDesc });
    setNewName(""); setNewDesc("");
  }, `Team '${newName}' created`);

  const sendInvite = () => act(async () => {
    const r = await api.post("/admin/dev/invite", { email: invEmail, name: invName, team_ids: invTeams });
    setInviteOpen(false); setInvEmail(""); setInvName(""); setInvTeams([]);
    return r;
  });

  const devCandidates = (team) =>
    users.filter((u) => (u.role === "dev" || u.role === "admin") && !(team.members || []).some((m) => m.id === u.id));

  const pendingInvites = invites.filter((i) => i.status === "pending");

  return (
    <div data-testid="admin-dev-teams" className="flex flex-col gap-5">
      {/* Header + invite */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[10px] text-zinc-500 leading-relaxed max-w-xl">
          Teams control which documents a dev can see in the Dev Hub. Global docs stay visible to every dev — team docs only to members.
        </p>
        <button data-testid="invite-dev-button" onClick={() => setInviteOpen(true)} className="bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2.5 flex items-center gap-2 transition-colors">
          <MailPlus className="w-3.5 h-3.5" /> Invite a Dev
        </button>
      </div>

      {/* Create team */}
      <div className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-3">
        <p className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2"><Users className="w-3 h-3" /> New Team</p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-2">
          <input data-testid="team-name-input" className={inputCls} placeholder="Team name (e.g. Living Dead)" value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={60} />
          <input data-testid="team-desc-input" className={inputCls} placeholder="Description (optional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} maxLength={300} />
          <button data-testid="team-create-button" disabled={busy || newName.trim().length < 2} onClick={createTeam} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2 transition-colors">Create</button>
        </div>
      </div>

      {/* Team cards */}
      {teams.map((t, ti) => (
        <div key={t.id} data-testid={`team-card-${ti + 1}`} className="border border-white/10 bg-[#050505]/50 p-4 flex flex-col gap-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-sans font-bold text-sm text-zinc-100 uppercase flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-nc-blue" /> {t.name}
                <span className="font-mono text-[9px] text-zinc-500 normal-case">// {(t.members || []).length} member(s)</span>
              </p>
              {t.description && <p className="font-mono text-[10px] text-zinc-500 mt-1">{t.description}</p>}
            </div>
            {confirmDelete === t.id ? (
              <div className="flex items-center gap-2 border border-red-500/50 bg-red-500/10 px-3 py-1.5">
                <span className="font-mono text-[9px] text-red-300 uppercase">Docs go global — sure?</span>
                <button data-testid={`team-delete-confirm-${ti + 1}`} disabled={busy} onClick={() => act(() => api.delete(`/admin/dev/teams/${t.id}`)).then(() => setConfirmDelete(""))} className="font-mono text-[9px] uppercase px-2 py-1 bg-red-500 hover:bg-red-600 text-white transition-colors">Delete</button>
                <button onClick={() => setConfirmDelete("")} className="font-mono text-[9px] uppercase px-2 py-1 text-zinc-400">No</button>
              </div>
            ) : (
              <button data-testid={`team-delete-${ti + 1}`} onClick={() => setConfirmDelete(t.id)} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-2 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(t.members || []).map((m) => (
              <span key={m.id} data-testid={`team-${ti + 1}-member-${m.email}`} className="font-mono text-[10px] text-zinc-200 border border-white/15 bg-white/5 px-2 py-1 flex items-center gap-1.5">
                {m.name || m.email}
                <button data-testid={`team-${ti + 1}-remove-${m.email}`} disabled={busy} onClick={() => act(() => api.delete(`/admin/dev/teams/${t.id}/members/${m.id}`))} className="text-zinc-500 hover:text-red-400 transition-colors" title="Remove from team"><X className="w-3 h-3" /></button>
              </span>
            ))}
            {(t.members || []).length === 0 && <span className="font-mono text-[10px] text-zinc-600">No members yet.</span>}
          </div>
          <div className="flex gap-2">
            <select data-testid={`team-${ti + 1}-member-select`} className={inputCls} value={memberPick[t.id] || ""} onChange={(e) => setMemberPick((p) => ({ ...p, [t.id]: e.target.value }))}>
              <option value="">Add a dev to this team…</option>
              {devCandidates(t).map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
            </select>
            <button data-testid={`team-${ti + 1}-member-add`} disabled={busy || !memberPick[t.id]} onClick={() => act(async () => { await api.post(`/admin/dev/teams/${t.id}/members`, { user_id: memberPick[t.id] }); setMemberPick((p) => ({ ...p, [t.id]: "" })); })} className="shrink-0 bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-[10px] px-4 py-2 flex items-center gap-2 transition-colors">
              <UserPlus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>
      ))}
      {teams.length === 0 && <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">No teams yet — create one above.</p>}

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div className="border border-nc-orange/30 bg-nc-orange/5 p-4 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase flex items-center gap-2"><Clock className="w-3 h-3" /> Pending Invitations ({pendingInvites.length})</p>
          {pendingInvites.map((inv, i) => (
            <div key={inv.id} data-testid={`pending-invite-${i + 1}`} className="flex items-center justify-between gap-3 border border-white/10 bg-black/40 px-3 py-2">
              <div className="min-w-0">
                <p className="font-mono text-xs text-zinc-200 truncate">{inv.email}</p>
                <p className="font-mono text-[9px] text-zinc-500">{(inv.team_names || []).join(", ") || "general pool"} // sent {String(inv.created_at).slice(0, 10)} // expires {String(inv.expires_at).slice(0, 10)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {inv.link && (
                  <button data-testid={`pending-invite-copy-${i + 1}`} onClick={async () => { (await safeCopy(inv.link)) ? toast.success("Invite link copied") : toast.error("Copy blocked"); }} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 transition-colors">Copy Link</button>
                )}
                <button data-testid={`pending-invite-resend-${i + 1}`} disabled={busy} onClick={() => act(async () => { const r = await api.post(`/admin/dev/invites/${inv.id}/resend`); await safeCopy(r.data.link || ""); return r; }, "Invitation re-sent — fresh link copied to clipboard")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 transition-colors">Resend</button>
                <button data-testid={`pending-invite-revoke-${i + 1}`} disabled={busy} onClick={() => act(() => api.delete(`/admin/dev/invites/${inv.id}`), "Invitation revoked")} className="font-mono text-[9px] tracking-[0.15em] uppercase px-3 py-1.5 border border-red-500/50 text-red-400 hover:bg-red-500/10 transition-colors">Revoke</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" onClick={() => setInviteOpen(false)}>
          <div data-testid="invite-dev-modal" className="border border-nc-blue/40 bg-[#0a0a0a] max-w-md w-full p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><MailPlus className="w-4 h-4" /> Invite a Dev</p>
              <button data-testid="invite-dev-close" onClick={() => setInviteOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
              An invitation email is sent automatically. If they already have an account, dev access is granted instantly — otherwise they get a 7-day invite link that activates dev access after they register.
            </p>
            <div>
              <label className={labelCls}>Their email *</label>
              <input data-testid="invite-email-input" type="email" className={inputCls} placeholder="dev@example.com" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Their name (optional)</label>
              <input data-testid="invite-name-input" className={inputCls} placeholder="e.g. Alex" value={invName} onChange={(e) => setInvName(e.target.value)} maxLength={60} />
            </div>
            <div>
              <label className={labelCls}>Assign to teams</label>
              <div className="flex flex-wrap gap-2">
                {teams.map((t) => (
                  <button key={t.id} type="button" data-testid={`invite-team-${t.name.replace(/\s/g, "-")}`} onClick={() => setInvTeams((s) => s.includes(t.id) ? s.filter((x) => x !== t.id) : [...s, t.id])} className={`font-mono text-[10px] tracking-[0.15em] uppercase px-3 py-1.5 border transition-colors ${invTeams.includes(t.id) ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}>
                    {t.name}
                  </button>
                ))}
                {teams.length === 0 && <span className="font-mono text-[10px] text-zinc-600">No teams yet — they will join the general dev pool.</span>}
              </div>
            </div>
            <button data-testid="invite-send-button" disabled={busy || !invEmail.includes("@")} onClick={sendInvite} className="bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-4 py-3 flex items-center justify-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> Send Invitation Email
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
